import { useMemo } from 'react';

import type { JSONContent } from '@tiptap/core';

import { AuthorImagesElement, type TAuthorImagesElement } from '@/authorImagesElement';
import { ParagraphElement, type TParagraphElement } from '@/paragraphElement';
import { getAuthorNamesTextNodes } from '@/paragraphElement/utils/getAuthorNamesText';
import type { SerializableNode } from '@/serializer';
import { SerializeTextLeaf } from '@/textLeaf';
import type { Author, DreamRenderElementProps, PostHeaderData, UTMAttrs } from '@/types';
import { evaluateAndAppendUTMParams } from '@/utils/evaluateAndAppendUTMParams';

interface Props {
  authors: Author[];
  authorUrls?: PostHeaderData['author_urls'];
  node: JSONContent & SerializableNode;
  utmParams?: UTMAttrs;
  attributes?: DreamRenderElementProps<TParagraphElement>['attributes'];
}

export const PostAuthors = ({ authors, authorUrls, node, utmParams, attributes }: Props) => {
  const authorUrlsWithUTMParams = useMemo(() => {
    if (!authorUrls) {
      return authorUrls;
    }

    return Object.entries(authorUrls).reduce(
      (acc, [key, value]) => {
        acc[key] = evaluateAndAppendUTMParams(value, utmParams) || value;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [authorUrls, utmParams]);

  const textNodes = getAuthorNamesTextNodes({
    authors: authors ?? [],
    authorUrls: authorUrlsWithUTMParams as PostHeaderData['author_urls'],
    truncateList: node.attrs?.postAuthorsTruncateListEnabled ?? false,
    byLineText: node.attrs?.postAuthorsByLine ?? '',
    // @ts-expect-error
    textStyleAttrs: node?.content?.[0]?.marks?.[0]?.attrs,
  });

  return (
    <ParagraphElement element={node as TParagraphElement} attributes={attributes} as="div">
      <div className="flex flex-wrap items-center" style={{ gap: node?.attrs?.postAuthorsGap ?? '2px' }}>
        {node?.attrs?.postAuthorsImagesEnabled && (
          <AuthorImagesElement
            element={
              {
                ...node,
                attrs: {
                  ...node.attrs,
                  size: node?.attrs?.postAuthorsImageSize ?? '24px',
                  fill: node?.attrs?.postAuthorsImagesFill ?? 'inherit',
                  authors,
                },
              } as TAuthorImagesElement
            }
          >
            {null}
          </AuthorImagesElement>
        )}

        {(node?.attrs?.postAuthorsTextEnabled ?? true) ? (
          <span>
            {textNodes.map((textNode, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: textNodes may not have stable IDs, safe to use index here
              <SerializeTextLeaf key={index} leaf={textNode} />
            ))}
          </span>
        ) : null}
      </div>
    </ParagraphElement>
  );
};
